import React, { useRef, useState } from 'react'
import {
  Button,
  Input,
  Notification,
  NotificationType,
  Content,
  Textarea
} from '@ampcore/adel-react'
import xss from 'xss'
import {
  isValidEmail,
  formatPhoneNumber,
  isValidPhoneNumber
} from '../../../common/utils'
import { formSelectedData, CONSTs } from '../views/utils'

export type PAFieldName =
  | 'firstname'
  | 'lastname'
  | 'emailaddress'
  | 'phone'
  | 'profession'
  | 'inquiry'

export type PAFormData = {
  firstname: string | null
  lastname: string | null
  emailaddress: string | null
  phone: string | null
  profession: string | null
  inquiry: string | null
}

export type PAErrorFields = {
  firstname: boolean
  lastname: boolean
  emailaddress: boolean
  phone: boolean
  profession: boolean
  inquiry: boolean
  emailWrongFormat: boolean
}

export type PAFormLabels = {
  firstname: string
  lastname: string
  emailaddress: string
  phone: string
  profession: string
  inquiry: string
  buttonlabel: string
  buttonLoadingLabel: string
}

export type PAFormHeading = {
  title?: string
  introBody?: string
  disclaimer?: string
}

export type PAFormEmailInfo = {
  emailApi: string
  emailFormHeading: string
  emailGreeting: string
  emailIntro: string
  subject: string
  toEmailAddress: string
}

export type PAFormErrorMessages = {
  errorFirstname: string
  errorLastname: string
  errorEmailaddress: string
  errorEmailaddressWrongFormat: string
  errorPhone: string
}

export type PAServiceMessages = {
  serviceSuccess: string
  serviceSuccessThankyou: string
  serviceFailed: string
}

export type PAFormPleaseNote = {
  message: string
}

export type PAShowServerResponse = (props: unknown, message?: string) => void

export type ContactFormProfessionalAllianceContent = {
  formLabels: PAFormLabels
  formHeading?: PAFormHeading
  formEmailInfo: PAFormEmailInfo
  formErrorMessages: PAFormErrorMessages
  serviceMessages?: Partial<PAServiceMessages>
  formPleaseNote?: PAFormPleaseNote
  formTemplate?: string
}

export type ContactFormProfessionalAllianceCallback = (
  items: unknown,
  emailInfo: PAFormEmailInfo,
  showServerResponse: PAShowServerResponse,
  showEmailIntro: boolean
) => unknown

export type ContactFormProfessionalAllianceProps = {
  callback?: ContactFormProfessionalAllianceCallback
  name?: string
  content: ContactFormProfessionalAllianceContent
  testId?: string
}

const ContactFormProfessionalAlliance = (
  props: ContactFormProfessionalAllianceProps
) => {
  const { callback, name, content, testId } = props

  const [formData, setFormData] = useState<PAFormData>({
    firstname: null,
    lastname: null,
    emailaddress: null,
    phone: null,
    profession: null,
    inquiry: null
  })

  const [errorFields, setErrorFields] = useState<PAErrorFields>({
    firstname: false,
    lastname: false,
    emailaddress: false,
    phone: false,
    profession: false,
    inquiry: false,
    emailWrongFormat: false
  })

  const [showServiceFailedMessage, setShowServiceFailedMessage] =
    useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [loading, setLoading] = useState(false)

  const firstnameRef = useRef<HTMLInputElement | null>(null)
  const lastnameRef = useRef<HTMLInputElement | null>(null)
  const emailaddressRef = useRef<HTMLInputElement | null>(null)
  const phoneRef = useRef<HTMLInputElement | null>(null)

  const showServerResponse: PAShowServerResponse = (props, message) => {
    if (!props || !message) return
    if (message === CONSTs.SUCCESS) {
      setShowSuccessMessage(true)
      setShowServiceFailedMessage(false)
    } else {
      setShowSuccessMessage(false)
      setShowServiceFailedMessage(true)
    }
    setLoading(false)
  }

  const getFormStatus = (): boolean => {
    let isFormValid = true
    for (const key in formData) {
      const typedKey = key as keyof PAFormData
      if (
        typedKey !== 'phone' &&
        typedKey !== 'profession' &&
        typedKey !== 'inquiry' &&
        formData[typedKey] === null
      ) {
        isFormValid = false
      }
    }
    return !errorFields.phone && isFormValid
  }

  const validateField = (fieldName: PAFieldName, value: string) => {
    const fieldErrors: PAErrorFields = { ...errorFields }
    const fieldValues: PAFormData = { ...formData }
    const isNotEmpty = value.trim().length > 0

    switch (fieldName) {
    case 'firstname': {
      fieldErrors.firstname = !isNotEmpty
      fieldValues.firstname = isNotEmpty ? value : null
      break
    }
    case 'lastname': {
      fieldErrors.lastname = !isNotEmpty
      fieldValues.lastname = isNotEmpty ? value : null
      break
    }
    case 'emailaddress': {
      const isEmailValid = isValidEmail(value)
      fieldErrors.emailaddress = !isEmailValid
      fieldErrors.emailWrongFormat = !isEmailValid && isNotEmpty
      fieldValues.emailaddress = isEmailValid ? value : null
      break
    }
    case 'phone': {
      const isValidPhone = isValidPhoneNumber(value, true)
      if (isValidPhone) {
        const formattedPhone = formatPhoneNumber(value)
        if (phoneRef.current) {
          phoneRef.current.value = formattedPhone
        }
        fieldValues.phone = formattedPhone
        fieldErrors.phone = false
      } else {
        fieldErrors.phone = true
        fieldValues.phone = null
      }
      break
    }
    case 'profession': {
      fieldValues.profession = value || null
      break
    }
    case 'inquiry': {
      fieldValues.inquiry = value || null
      break
    }
    }

    setFormData(fieldValues)
    setErrorFields(fieldErrors)
  }

  const handleFieldChange = (event: React.SyntheticEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null
    if (!target || !target.name) return
    validateField(target.name as PAFieldName, target.value || '')
  }

  const submitData = (event: { preventDefault: () => void }): void => {
    event.preventDefault()

    const canFormBeSubmitted = getFormStatus()

    if (canFormBeSubmitted) {
      setLoading(true)
      const items = formSelectedData(formData, content.formTemplate)
      const showEmailIntroMessage = true
      const { emailGreeting, emailIntro, emailFormHeading } =
        content.formEmailInfo
      const updatedEmailInfo: PAFormEmailInfo = {
        ...content.formEmailInfo,
        emailIntro: `${emailGreeting},<br>${emailIntro}<br><br><b>${emailFormHeading}</b><br>`
      }
      if (callback && typeof callback === 'function' && items) {
        callback(
          items,
          updatedEmailInfo,
          showServerResponse,
          showEmailIntroMessage
        )
      }
      return
    }

    const formFirstName = firstnameRef.current?.value || ''
    const formLastName = lastnameRef.current?.value || ''
    const formEmailAddress = emailaddressRef.current?.value || ''
    const formPhoneNumber = phoneRef.current?.value || ''
    const fieldValues: PAFormData = { ...formData }
    const fieldErrors: PAErrorFields = { ...errorFields }

    if (formFirstName.length < 1) {
      fieldValues.firstname = null
      fieldErrors.firstname = true
    } else {
      fieldValues.firstname = formData.firstname
      fieldErrors.firstname = false
    }

    if (formLastName.length < 1) {
      fieldValues.lastname = null
      fieldErrors.lastname = true
    } else {
      fieldValues.lastname = formData.lastname
      fieldErrors.lastname = false
    }

    if (formEmailAddress) {
      const cleanedText = formEmailAddress.toLowerCase()
      fieldValues.emailaddress = isValidEmail(cleanedText)
        ? formEmailAddress
        : null
      fieldErrors.emailaddress = !fieldValues.emailaddress
    } else {
      fieldErrors.emailaddress = true
    }

    if (formPhoneNumber) {
      fieldValues.phone = isValidPhoneNumber(formPhoneNumber, true)
        ? formPhoneNumber
        : null
      fieldErrors.phone = !fieldValues.phone
    }

    setFormData(fieldValues)
    setErrorFields(fieldErrors)
  }

  const setEmailErrorMessage = (
    wrongEmail: boolean,
    message: PAFormErrorMessages
  ): string => {
    return wrongEmail
      ? message.errorEmailaddressWrongFormat
      : message.errorEmailaddress
  }

  const successMessage = (
    <>
      <h3 className="u-paddingBottom16">
        {content.serviceMessages?.serviceSuccessThankyou}
      </h3>
      <p
        className="u-paddingTop16 u-paddingBottom16"
        dangerouslySetInnerHTML={{
          __html: xss(content.serviceMessages?.serviceSuccess ?? '')
        }}
      />
    </>
  )

  return (
    <div className="u-marginTop32">
      {showSuccessMessage && content.serviceMessages?.serviceSuccess && (
        <Notification
          type={NotificationType.CONFIRM}
          dismissable={false}
          className="PAFormNotification u-sizeConstrained"
          children={successMessage}
        />
      )}
      {!showSuccessMessage && (
        <div className="u-sizeConstrained">
          <div className="u-lg-size8of12 u-lg-before2of12 u-size10of12 u-md-before1of12 u-sm-sizeFull u-paddingBottom8">
            <h3 className="u-textDefault u-textCenter u-textSecondary u-textNormal u-paddingBottom40">
              {content.formHeading?.title}
            </h3>
            <div className="u-bgColorLight">
              <div className="u-paddingHoriz40 u-paddingTop40">
                <h4 className="u-textDefault u-textColorBrand u-textSecondary u-textNormal u-lg-textSizeMsrp1 u-md-textSizeMsrp2 u-sm-textSizeMsrp1 u-textCenter">
                  {content.formHeading?.introBody}
                </h4>
              </div>
              <div>
                <form
                  name={name}
                  className="u-padding40"
                  onBlur={handleFieldChange}
                  onKeyUp={handleFieldChange}
                >
                  <hr />
                  <div className="u-paddingTop24">
                    <div className="u-flex u-flexRow u-sm-flexCol">
                      <div className="u-size6of12 u-sm-sizeFull">
                        <Input
                          name="firstname"
                          id="firstname"
                          label={content.formLabels.firstname}
                          reference={firstnameRef}
                          error={errorFields.firstname}
                          message={content.formErrorMessages.errorFirstname}
                          maxLength={100}
                          testId={testId ? `${testId}-firstname` : undefined}
                        />
                      </div>
                      <div className="u-size6of12 u-lg-before1of12 u-md-before1of12 u-sm-sizeFull">
                        <Input
                          name="lastname"
                          id="lastname"
                          label={content.formLabels.lastname}
                          reference={lastnameRef}
                          error={errorFields.lastname}
                          message={content.formErrorMessages.errorLastname}
                          maxLength={100}
                          testId={testId ? `${testId}-lastname` : undefined}
                        />
                      </div>
                    </div>
                    <div className="u-flex u-flexRow u-sm-flexCol">
                      <div className="u-size6of12 u-sm-sizeFull">
                        <Input
                          name="emailaddress"
                          id="email"
                          label={content.formLabels.emailaddress}
                          reference={emailaddressRef}
                          error={errorFields.emailaddress}
                          message={setEmailErrorMessage(
                            errorFields.emailWrongFormat,
                            content.formErrorMessages
                          )}
                          testId={testId ? `${testId}-email` : undefined}
                        />
                      </div>
                      <div className="u-size6of12 u-lg-before1of12 u-md-before1of12 u-sm-sizeFull">
                        <Input
                          name="phone"
                          id="phone"
                          placeholder=""
                          label={content.formLabels.phone}
                          reference={phoneRef}
                          error={errorFields.phone}
                          message={content.formErrorMessages.errorPhone}
                          testId={testId ? `${testId}-phone` : undefined}
                        />
                      </div>
                    </div>
                    <div className="u-flex u-flexRow u-sm-flexCol">
                      <div className="u-size6of12 u-sm-sizeFull">
                        <Input
                          name="profession"
                          id="profession"
                          label={content.formLabels.profession}
                          maxLength={100}
                          testId={testId ? `${testId}-profession` : undefined}
                        />
                      </div>
                      <div className="u-size6of12 u-lg-before1of12 u-md-before1of12 u-sm-sizeZero" />
                    </div>
                    <div className="u-flex u-flexRow u-sm-flexCol">
                      <div className="u-sizeFull">
                        <Textarea
                          name="inquiry"
                          id="inquiry"
                          label={content.formLabels.inquiry}
                          row={3}
                          maxLength={250}
                          testId={testId ? `${testId}-inquiry` : undefined}
                        />
                      </div>
                    </div>
                    {content.formHeading?.disclaimer && (
                      <div
                        className="Disclaimer-text u-marginBottom24"
                        dangerouslySetInnerHTML={{
                          __html: xss(content.formHeading.disclaimer)
                        }}
                      />
                    )}
                  </div>
                  {showServiceFailedMessage &&
                    content.serviceMessages?.serviceFailed && (
                    <Notification
                      type={NotificationType.ALERT}
                      message={content.serviceMessages.serviceFailed}
                      dismissable={false}
                      className="u-marginBottom24"
                    />
                  )}
                  <Button
                    onClick={submitData}
                    isLoading={loading}
                    data-analytics={'Button'}
                    data-tracking={'Button'}
                    children={
                      loading
                        ? content.formLabels.buttonLoadingLabel
                        : content.formLabels.buttonlabel
                    }
                  />
                </form>
              </div>
            </div>
            {content.formPleaseNote?.message && (
              <Content className={'u-marginBottom24'}>
                <div
                  className="Content"
                  dangerouslySetInnerHTML={{
                    __html: xss(content.formPleaseNote.message)
                  }}
                />
              </Content>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ContactFormProfessionalAlliance
